import {
  AmountInput,
  Button,
  FormField,
  SkeletonText,
  SwitchField,
} from '@dev-spendesk/grapes';
import { useFormik } from 'formik';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import { useNotifications } from 'modules/app/notifications';
import { NotificationType } from 'modules/app/notifications/notification';
import { useModalState } from 'src/core/common/hooks/useModalState';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useUserRoles } from 'src/core/modules/app/hooks/useUserRoles';
import { useFetchCompanyInstantPaymentLimits } from 'src/core/modules/company/hooks';
import { getCurrency } from 'src/core/modules/company/redux/selectors';
import { formatMoney, getCurrencyOptionByKey } from 'src/core/utils/money';

import { UpdateInstantPaymentLimitsModal } from './UpdateInstantPaymentLimitsModal';

type DailyLimitFormValues = {
  dailyLimit: number | null;
};

export const DailyLimitSection = () => {
  const { t } = useTranslation('global');
  const { pushNotif } = useNotifications();
  const { isAccountOwner } = useUserRoles();
  const currency = useSelector(getCurrency);

  const {
    data: instantPaymentLimitsData,
    isLoading: isFetchingInstantPaymentLimits,
    isDailyLimitEnabled,
    disableInstantPaymentDailyLimits,
    enableInstantPaymentDailyLimits,
    updateInstantPaymentDailyLimits,
  } = useFetchCompanyInstantPaymentLimits();

  const pendingActionHandler = useRef<(() => Promise<void>) | null>(null);

  const updateInstantPaymentLimitsModalState = useModalState({
    initialState: false,
  });

  const openWarningModal = (handler: () => Promise<void>) => {
    pendingActionHandler.current = handler;
    updateInstantPaymentLimitsModalState.open();
  };

  const closeWarningModal = () => {
    formik.setSubmitting(false);
    pendingActionHandler.current = null;
    updateInstantPaymentLimitsModalState.close();
  };

  const handleWarningModalConfirm = async () => {
    formik.setSubmitting(true);
    await pendingActionHandler.current?.();
    closeWarningModal();
  };

  const handleUpdateDailyLimit = async (dailyLimit: number) => {
    try {
      await updateInstantPaymentDailyLimits({
        dailyLimit,
      });
      pushNotif({
        type: NotificationType.Success,
        message: t(
          'generalSettings.instantPaymentLimits.dailyLimitSection.successNotification',
        ),
      });
    } catch {
      pushNotif({
        type: NotificationType.Danger,
        message: t('misc.errors.serverError'),
      });
    }
  };

  const handleSwitchChange = () => {
    if (!isDailyLimitEnabled) {
      openWarningModal(async () => {
        await enableInstantPaymentDailyLimits();
      });
      return;
    }
    openWarningModal(async () => {
      await disableInstantPaymentDailyLimits();
    });
  };

  const maxDailyLimit = instantPaymentLimitsData?.maxDailyLimit;
  const formattedMaxAmount = maxDailyLimit
    ? formatMoney(maxDailyLimit, currency)
    : '';
  const errorMessage = t(
    'generalSettings.instantPaymentLimits.dailyLimitSection.errorMessage',
    {
      amount: formattedMaxAmount,
    },
  );

  const dailyLimitSchema = yup.object({
    dailyLimit: yup
      .number()
      .nullable()
      .min(0, errorMessage)
      .max(maxDailyLimit ?? 0, errorMessage),
  });

  const formik = useFormik<DailyLimitFormValues>({
    initialValues: {
      dailyLimit: instantPaymentLimitsData?.dailyLimit ?? null,
    },
    validationSchema: dailyLimitSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const { dailyLimit } = values;
      if (dailyLimit === null) {
        return;
      }
      openWarningModal(() => handleUpdateDailyLimit(dailyLimit));
    },
  });

  const canEdit = isAccountOwner;

  return (
    <div className="flex flex-col">
      <SwitchField
        id="dailyInstantPaymentLimitToggle"
        fit="parent"
        isChecked={isDailyLimitEnabled}
        isDisabled={!canEdit}
        label={t(
          'generalSettings.instantPaymentLimits.dailyLimitSection.title',
        )}
        helpText={t(
          'generalSettings.instantPaymentLimits.dailyLimitSection.description',
        )}
        onChange={handleSwitchChange}
      />
      {isDailyLimitEnabled && (
        <div className="mt-16 flex flex-col gap-24">
          <form onSubmit={formik.handleSubmit}>
            <div className="flex items-start gap-8">
              {isFetchingInstantPaymentLimits ? (
                <SkeletonText width="200px" />
              ) : (
                <FormField
                  label={t(
                    'generalSettings.instantPaymentLimits.dailyLimitSection.title',
                  )}
                  alertMessage={formik.errors.dailyLimit}
                >
                  <AmountInput
                    name="dailyLimit"
                    fit="parent"
                    value={formik.values.dailyLimit}
                    currency={getCurrencyOptionByKey(currency)}
                    isDisabled={!canEdit}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </FormField>
              )}
              {canEdit && (
                <Button
                  className="mt-24"
                  type="submit"
                  variant="secondaryNeutral"
                  text={t(
                    'generalSettings.instantPaymentLimits.dailyLimitSection.submitButton',
                  )}
                  isDisabled={
                    !formik.dirty ||
                    formik.isSubmitting ||
                    Object.keys(formik.errors).length > 0
                  }
                  isLoading={formik.isSubmitting}
                />
              )}
            </div>
          </form>
        </div>
      )}
      <UpdateInstantPaymentLimitsModal
        isOpen={updateInstantPaymentLimitsModalState.isOpen}
        onClose={closeWarningModal}
        onConfirm={handleWarningModalConfirm}
      />
    </div>
  );
};
